import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';

import {
  attachItemImage,
  getItemImageBlob,
  getItemImages,
  itemKeys,
  removeItemImage,
} from './itemsApi.ts';
import { normaliseItemName } from './normaliseItemName.ts';
import { isRetryable } from '../../offline/isOfflineError.ts';
import { enqueue } from '../../offline/mutationQueue.ts';

/**
 * Photos, from the client's side.
 *
 * The server files a photo under the normalised name within a group, so what the list
 * needs is not a field on each row but one small catalogue it can look names up in. That
 * is what `useItemImages` fetches, and `imageFor` is the lookup — the same normalisation
 * the duplicate check already uses, so a row and its photo agree about what counts as the
 * same thing.
 */
export function useItemImages(groupId: string | undefined) {
  return useQuery({
    queryKey: itemKeys.images(groupId ?? ''),
    queryFn: () => getItemImages(groupId!),
    enabled: Boolean(groupId),
    // Cheap and small, but there is no reason to refetch it on every focus: the hub
    // invalidates it the moment anyone in the household changes a photo.
    staleTime: 5 * 60_000,
  });
}

/** The photo for this item's name, if the household has one. */
export function imageFor(
  catalogue: { images: { id: string; name: string; width: number; height: number }[] } | undefined,
  name: string,
) {
  if (!catalogue) {
    return undefined;
  }

  const normalised = normaliseItemName(name);

  return catalogue.images.find((image) => image.name === normalised);
}

export function invalidateItemImages(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: itemKeys.allImages });
}

/**
 * Attaching a photo.
 *
 * Applied on the tap rather than on Save, like every other switch in the app: the photo
 * belongs to the name and not to the row being edited, Save is the thing that can change
 * that name, and deferring the upload would race the two inside one submit. It also puts
 * the feedback where the waiting is — compressing and uploading is the slow part, and
 * hiding it behind a Save button would make Save look broken.
 */
export interface AttachImage {
  listId: string;
  itemId: string;
  imageId: string;
  blob: Blob;
}

export function useAttachImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ itemId, imageId, blob }: AttachImage) =>
      attachItemImage(itemId, imageId, blob),

    onError: async (error, { listId, itemId, imageId, blob }) => {
      // Queued when we never reached the server, thrown on when the server refused.
      // The distinction matters more here than anywhere else in the app: everything else
      // the queue holds can be redone with one tap, and a photograph of a shelf you have
      // already walked away from cannot.
      if (isRetryable(error)) {
        await enqueue({ kind: 'attachImage', listId, itemId, imageId, blob });
        return;
      }

      throw error;
    },

    onSuccess: () => invalidateItemImages(queryClient),
  });
}

export function useRemoveImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ itemId }: { listId: string; itemId: string }) => removeItemImage(itemId),

    onError: async (error, { listId, itemId }) => {
      if (isRetryable(error)) {
        await enqueue({ kind: 'removeImage', listId, itemId });
        return;
      }

      throw error;
    },

    onSuccess: () => invalidateItemImages(queryClient),
  });
}

/**
 * The bytes for one photo.
 *
 * `staleTime: Infinity` because the id is immutable — replacing a photo mints a new one,
 * so these bytes can never change and there is nothing to revalidate.
 *
 * Deliberately caches the <b>blob</b> and not an object URL. A URL has to be revoked or
 * it pins the whole photograph in memory for the life of the page, and React Query
 * evicting a cache entry does not run any cleanup of ours — so the URL's lifetime belongs
 * to whichever component is showing it. See `useObjectUrl`.
 */
export function useItemImageBlob(groupId: string | undefined, imageId: string | undefined) {
  return useQuery({
    queryKey: ['item-image-blob', groupId ?? '', imageId ?? ''],
    queryFn: () => getItemImageBlob(groupId!, imageId!),
    enabled: Boolean(groupId) && Boolean(imageId),
    staleTime: Infinity,
    gcTime: 10 * 60_000,
  });
}

/**
 * An object URL that lives exactly as long as the component showing it.
 *
 * Created when the blob arrives and revoked on unmount or when the blob changes, which is
 * the only way to be sure a photo is not still held after the viewer closes.
 */
export function useObjectUrl(blob: Blob | undefined): string | undefined {
  // Derived, not stored. Holding it in state would mean setting that state from an
  // effect, which the React Compiler lint rules in this repo treat as an error — and
  // rightly: the URL is a pure function of the blob, so it is a computation.
  const url = useMemo(() => (blob ? URL.createObjectURL(blob) : undefined), [blob]);

  const pendingRevoke = useRef<number | undefined>(undefined);

  // The effect exists only to release it. Revoking is what stops a viewed photograph
  // being pinned in memory for the life of the page.
  //
  // The revoke is deferred by a tick, and a re-registration cancels it. Without that,
  // StrictMode's development double-run tore down a URL that the very next effect run then
  // re-registered — the memo is not recomputed by the simulated remount, so the component
  // was left holding a revoked URL and drew a broken image. It only showed up when the blob
  // was already cached at mount, which is exactly the photo viewer opened a second time.
  useEffect(() => {
    if (!url) {
      return;
    }

    window.clearTimeout(pendingRevoke.current);

    return () => {
      pendingRevoke.current = window.setTimeout(() => URL.revokeObjectURL(url), 0);
    };
  }, [url]);

  return url;
}
